// src/stores/videoStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchData } from '@/composables/fetchData'
import { useAuth } from '@/composables/useAuth'
import { useAdStore } from './adStore'
import { useConfigStore } from './configStore'
import { getNextEpisode, generateSlug, addQueryParam, buildFeedUrl } from '@/utils'


const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID
const showPartners = import.meta.env.VITE_SHOW_PARTNERS?.toLowerCase?.() === 'true'

export const useVideoStore = defineStore('videoStore', () => {
  const configStore = useConfigStore()
  const { user } = useAuth()
  const videos = ref([])
  const selectedVideo = ref(null)
  const selectedSeries = ref(null)
  const selectedSection = ref(null)
  const loading = ref(false)
  const currentFeedId = ref(null);
  const adStore = useAdStore()

  async function loadVideos(feedId = defaultFeedId, query = '', forceReload = false) {
    const shouldSkipReload = !forceReload
      && feedId === currentFeedId.value
      && !query
      && videos.value.length > 0;

    if (shouldSkipReload) return;

    const user_id = user.value?.app_metadata?.mongo_id;
    if (user.value !== undefined && !user_id) {
      // When user value is an empty object, it means user just logged in, we skip this call as user_id is not set yet
      // For logged out users, user value is undefined instead
      console.log("Not user_id")
    }

    loading.value = true

    let url = buildFeedUrl(feedId, query, user_id);

    try {
      const data = await fetchData(url);
      if (!data || !Array.isArray(data.sections)) {
        console.warn('No valid sections found in the response');
        videos.value = [];
        return;
      }

      videos.value = processSections(data);

      if (data?._config) {
        // Handle ads configuration
        if (data._config.adsRate?.intervalMiliSeconds) {
          adStore.setupAdConfig(data._config.adsRate.intervalMiliSeconds)
        }

        // Handle Auth0 client ID configuration
        if (data._config.authClientId) {
          configStore.setAuthClientId(data._config.authClientId)
        }

        if (data._config.features) {
          configStore.setFeatures(data._config.features)
        }

      }

      currentFeedId.value = feedId;
    } catch (error) {
      console.error(error)
      console.error('Error loading videos:', error.message);
      videos.value = [];
    } finally {
      loading.value = false;
    }
  }

  const processSections = (data) =>
    data.sections.map((section) => {
      const sectionData = data[section.key] || [];
      if (section.name.toLowerCase() === 'partners' && showPartners) {
        return {
          name: section.name,
          type: section.type,
          key: section.key,
          partners: sectionData.map(partner => ({
            id: partner.id,
            title: partner.title,
            shortDescription: partner.shortDescription,
            longDescription: partner.longDescription,
            thumbnail: partner.thumbnail,
            heroImage: partner.heroImage
          }))
        };
      }

      switch (section.type) {
        case 'singlevideo':
          return {
            name: section.name,
            type: section.type,
            key: section.key,
            videos: sectionData
          };
        case 'playlist':
          return {
            name: section.name,
            type: section.type,
            series: sectionData.map(series => ({
              title: series.title,
              shortDescription: series.shortDescription,
              longDescription: series.longDescription,
              thumbnail: series.thumbnail,
              thumbnail2x3: series.thumbnail2x3,
              heroImage: series.heroImage,
              id: series.id,
              genres: series.genres,
              seasons: (series.seasons || []).map(season => ({
                seasonNumber: season.seasonNumber,
                episodes: (season.episodes || []).map(episode => ({
                  ...episode,
                  duration: episode.content?.duration || 0,
                  url: episode.content?.videos?.[0]?.url || ''
                }))
              }))
            }))
          };
        default:
          return { name: section.name, type: section.type, videos: [] };
      }
    })


  function selectVideo(video) {
    selectedVideo.value = video
  }

  function selectSection(section) {
    selectedSection.value = section
  }

  function selectSeries(seriesSlug) {
    const allSeries = videos.value
      .filter(section => section.type === 'playlist')
      .flatMap(section => section.series || [])
    const series = allSeries.find(series => generateSlug(series.title, series.id) === seriesSlug);
    if (series) {
      selectedSeries.value = {
        title: series.title,
        shortDescription: series.shortDescription,
        longDescription: series.longDescription,
        thumbnail: series.thumbnail,
        thumbnail2x3: series.thumbnail2x3,
        heroImage: series.heroImage,
        id: series.id,
        genres: series.genres,
        seasons: series.seasons
      }
    } else {
      selectedSeries.value = null
    }
    return selectedSeries.value
  }

  const allSingleVideos = computed(() => {
    return videos.value
      .filter(section => section.type === 'singlevideo')
      .flatMap(section => section.videos)
  })

  const allVideosAndEpisodes = computed(() => {
    const singleVideos = videos.value
      .filter((section) => section.type === 'singlevideo')
      .flatMap((section) => section.videos);
    const episodes = videos.value
      .filter((section) => section.type === 'playlist')
      .flatMap((section) => section.series || [])
      .flatMap((series) => series.seasons || [])
      .flatMap((season) => season.episodes || []);
    return [...singleVideos, ...episodes];
  });

  const allRentals = computed(() => {
    return videos.value
      .filter((section) => section.type === 'singlevideo')
      .flatMap((section) => section.videos)
      .filter((video) => video.is_rented);
  })

  function markVideoAsRented(videoId, rentalData) {
    // Find and update the video in the videos array
    videos.value.forEach(section => {
      if (section.type === 'singlevideo' && section.videos) {
        const videoIndex = section.videos.findIndex(video => video.id === videoId)
        if (videoIndex === -1) {
          return;
        }
        // Mark the video as rented
        section.videos[videoIndex].is_rented = true

        // Update rental information if provided
        if (rentalData) {
          section.videos[videoIndex].rented = {
            ...section.videos[videoIndex].rented,
            daysUntilExpiration: rentalData.daysUntilExpiration || "3",
            ...rentalData
          }
        }
      }
    })

    // Update selectedVideo if it's the same video
    if (selectedVideo.value && selectedVideo.value.id === videoId) {
      selectedVideo.value.is_rented = true
      if (rentalData) {
        selectedVideo.value.rented = {
          ...selectedVideo.value.rented,
          daysUntilExpiration: rentalData.daysUntilExpiration || "3",
          ...rentalData
        }
      }
    }
  }

  return {
    videos,
    selectedVideo,
    selectedSeries,
    selectedSection,
    loading,
    loadVideos,
    selectVideo,
    selectSeries,
    selectSection,
    allSingleVideos,
    allVideosAndEpisodes,
    allRentals,
    markVideoAsRented,
    getNextEpisode: (currentEpisodeId) => getNextEpisode(videos.value, currentEpisodeId),
  }
})
